/**
 * Readers for the parish data sheet's columns (release 19, owner 2026-10-08): the sheet itself
 * ("Data Umat" and its Google Form's "Form Responses 1") and any file written like it. Each turns
 * a cell into SATU's code the way ./normalise does the older columns, as a `Hasil`: `kosong` for
 * an empty cell, `nilai` for a reading, `gagal` for text that is there but names nothing, which
 * the row then empties with a note (the release 13 rule: the row is kept).
 *
 * The sheet's own words are read: "BPJS 1", "Rumah Pribadi", "Anggota"/"Bukan", "Anak" for a
 * child's baptism, the form's income bands ("Rp.2.000.000,- s/d Rp.3.000.000,-") and a typed
 * amount ("25000000"), a school written with its level ("SMA Negeri 1 Agats"), a Protestant
 * church by its name ("Adven", "GKI"). Nothing is guessed: a band across two of SATU's, "BPJS"
 * without its class, or a place under "Jenis Baptis" reads as nothing.
 */
import { bandPendapatan } from '../dataUmat'
import { PENDIDIKAN } from '../lookups/index'
import {
  AGAMA_UMAT,
  ASURANSI_KESEHATAN,
  type GOLONGAN_DARAH,
  JENIS_BAPTIS,
  JENIS_PERKAWINAN_LUAR,
  PENDAPATAN_BULANAN,
  PENDIDIKAN_SAAT_INI,
  STATUS_RUMAH,
} from '../schema/contract'
import ui from '../strings/ui.id.json' with { type: 'json' }
import { ANAK_KE_MAKS, TAHUN_LULUS_MAKS, TAHUN_LULUS_MIN } from '../validators/kolom'
import {
  bacaAwalan,
  bacaDenganAwalan,
  bacaKode,
  bacaTanggal,
  type Hasil,
  kunciTeks,
  PENDIDIKAN_ALIAS,
  rapikanTeks,
} from './normalise'

type AgamaUmat = (typeof AGAMA_UMAT)[number]
type GolonganDarah = (typeof GOLONGAN_DARAH)[number]
type PendapatanBulanan = (typeof PENDAPATAN_BULANAN)[number]
type AsuransiKesehatan = (typeof ASURANSI_KESEHATAN)[number]
type StatusRumah = (typeof STATUS_RUMAH)[number]
type JenisBaptis = (typeof JENIS_BAPTIS)[number]
type JenisPerkawinan = (typeof JENIS_PERKAWINAN_LUAR)[number]
type PendidikanSaatIni = (typeof PENDIDIKAN_SAAT_INI)[number]

const KOSONG: Hasil<never> = { jenis: 'kosong' }
const GAGAL: Hasil<never> = { jenis: 'gagal' }
const nilai = <T>(v: T): Hasil<T> => ({ jenis: 'nilai', nilai: v })

/**
 * A lookup of a list: each code, each label of `ui.enum` (as alias keys) and the extra words.
 * "BPJS Kelas 1" and "bpjs_kelas_1" read alike.
 */
function daftar<T extends string>(
  kode: readonly T[],
  label: Readonly<Record<string, string>>,
  alias: Readonly<Record<string, T>>,
): ReadonlyMap<string, T> {
  const peta = new Map<string, T>()
  for (const k of kode) {
    peta.set(kunciTeks(k), k)
    const l = label[k]
    if (l !== undefined) peta.set(kunciTeks(l), k)
  }
  for (const [kata, k] of Object.entries(alias)) peta.set(kata, k)
  return peta
}

function cari<T>(text: string | null | undefined, peta: ReadonlyMap<string, T>): Hasil<T> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const key = kunciTeks(t)
  if (key === '') return KOSONG
  const found = peta.get(key)
  return found === undefined ? GAGAL : nilai(found)
}

// ---------------------------------------------------------------------------
// Religion, and a Protestant church named instead of the religion
// ---------------------------------------------------------------------------

const AGAMA = daftar<AgamaUmat>(AGAMA_UMAT, ui.enum.agama, {
  katholik: 'katolik',
  catholic: 'katolik',
  rk: 'katolik',
  'roma katolik': 'katolik',
  'katolik roma': 'katolik',
  'kristen katolik': 'katolik',
  kristen: 'protestan',
  'kristen protestan': 'protestan',
  protestant: 'protestan',
  muslim: 'islam',
  moslem: 'islam',
  budha: 'buddha',
  budhis: 'buddha',
  buddhis: 'buddha',
  khonghucu: 'konghucu',
  'kong hu cu': 'konghucu',
  'khong hu cu': 'konghucu',
  konghuchu: 'konghucu',
  'aliran kepercayaan': 'kepercayaan',
  penghayat: 'kepercayaan',
  'penghayat kepercayaan': 'kepercayaan',
  'kepercayaan terhadap tuhan yme': 'kepercayaan',
  'kepercayaan terhadap tuhan yang maha esa': 'kepercayaan',
  lain: 'lainnya',
  'lain lain': 'lainnya',
})

/** The words that name a Catholic church in a longer cell ("Gereja Katolik"). */
const KATA_KATOLIK: ReadonlySet<string> = new Set(['katolik', 'katholik', 'catholic'])

/**
 * The words that name a Protestant church or its denomination ("Adven", "GKI Petra", "Gereja
 * Bethel"): the religion is Protestan, and the church's name is kept as Gereja asal.
 */
const KATA_PROTESTAN: ReadonlySet<string> = new Set([
  'kristen',
  'protestan',
  'adven',
  'advent',
  'gmahk',
  'gki',
  'gkj',
  'gkjw',
  'gkp',
  'gkps',
  'gkii',
  'gkst',
  'gpib',
  'gpi',
  'gpm',
  'gbi',
  'gbis',
  'gpdi',
  'gidi',
  'kingmi',
  'hkbp',
  'gmim',
  'gmit',
  'gmih',
  'gke',
  'gks',
  'pentakosta',
  'pantekosta',
  'bethel',
  'injili',
  'karismatik',
  'methodist',
  'metodis',
  'lutheran',
])

/** A religion: SATU's list, its everyday words, or a church whose words name its religion. */
export function bacaAgama(text: string | null | undefined): Hasil<AgamaUmat> {
  const hasil = cari(text, AGAMA)
  if (hasil.jenis !== 'gagal') return hasil
  const kata = kunciTeks(rapikanTeks(text) ?? '').split(' ')
  if (kata.some((w) => KATA_KATOLIK.has(w))) return nilai('katolik')
  if (kata.some((w) => KATA_PROTESTAN.has(w))) return nilai('protestan')
  return GAGAL
}

/**
 * The church a religion cell names ("Adven", "GKI Petra"), as written, for Gereja asal; null for
 * a religion's own word ("Protestan", "Kristen") or anything else.
 */
export function denominasiDari(text: string | null | undefined): string | null {
  const t = rapikanTeks(text)
  if (t === null) return null
  const baca = bacaAgama(t)
  if (baca.jenis !== 'nilai' || baca.nilai !== 'protestan') return null
  return AGAMA.get(kunciTeks(t)) === undefined ? t : null
}

// ---------------------------------------------------------------------------
// The sensitive fields (`umat_profil`): read for parish staff and admins only
// ---------------------------------------------------------------------------

/** "Tidak tahu" is a blood type's answer, not an empty cell (`rapikanTeks` empties it elsewhere). */
const TIDAK_TAHU: ReadonlySet<string> = new Set([
  'tidak tahu',
  'tdk tahu',
  'tidak tau',
  'tdk tau',
  'belum tahu',
  'blm tahu',
  'tidak diketahui',
  'tdk diketahui',
  'belum diketahui',
  'tidak tahu golongan darah',
])

/** A blood type: A, B, AB or O, its rhesus and "Gol." dropped; "Tidak tahu" kept as an answer. */
export function bacaGolonganDarah(text: string | null | undefined): Hasil<GolonganDarah> {
  if (text === null || text === undefined) return KOSONG
  const mentah = kunciTeks(text)
  if (TIDAK_TAHU.has(mentah)) return nilai('tidak_tahu')
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const key = kunciTeks(t)
    .split(' ')
    .filter(
      (w) =>
        !['golongan', 'gol', 'goldar', 'darah', 'rh', 'rhesus', 'positif', 'negatif'].includes(w),
    )
    .join(' ')
  if (key === '0' || key === 'o') return nilai('o')
  if (key === 'a' || key === 'b' || key === 'ab') return nilai(key)
  return GAGAL
}

/**
 * Lowercase words as typed ("flores"), each given its capital; a name typed with capitals is kept
 * as typed ("NTT", "Kei").
 */
export function rapikanSuku(text: string | null | undefined): string | null {
  const t = rapikanTeks(text)
  if (t === null) return null
  if (/\p{Lu}/u.test(t)) return t
  return t
    .split(' ')
    .map((w) => w.charAt(0).toLocaleUpperCase('id-ID') + w.slice(1))
    .join(' ')
}

const SATUAN: Readonly<Record<string, number>> = {
  rb: 1_000,
  ribu: 1_000,
  k: 1_000,
  jt: 1_000_000,
  juta: 1_000_000,
}

/** "2.000.000" (dots for thousands), "2,5" or "25000000.0" (a decimal), "750". */
function angkaRupiah(teks: string): number | null {
  if (/^\d{1,3}(?:[.,]\d{3})+$/.test(teks)) return Number(teks.replace(/[.,]/g, ''))
  if (/^\d+[.,]\d+$/.test(teks)) return Number(teks.replace(',', '.'))
  if (/^\d+$/.test(teks)) return Number(teks)
  return null
}

/** The amounts a cell writes, in rupiah: "500 rb–1 jt", "1–2 jt" (the unit after the second). */
function jumlahRupiah(s: string): number[] {
  const token: { angka: number; satuan: number | null }[] = []
  for (const m of s.matchAll(/(\d+(?:[.,]\d+)*)\s*(ribu|rb|juta|jt|k)?(?![a-z])/g)) {
    const angka = angkaRupiah(m[1] ?? '')
    if (angka === null) return []
    token.push({ angka, satuan: m[2] === undefined ? null : (SATUAN[m[2]] ?? null) })
  }
  return token.map((t, i) => {
    const berikut = token[i + 1]
    const satuan =
      t.satuan ?? (t.angka < 1000 && berikut?.satuan != null ? berikut.satuan : null) ?? 1
    return t.angka * satuan
  })
}

/**
 * A monthly income into SATU's band: the code; a band as the form words it ("< Rp.500.000,-",
 * "Rp.2.000.000,- s/d Rp.3.000.000,-", "> Rp.6.000.000,-") or as SATU does ("1–2 jt"); a typed
 * amount ("25000000", "Rp 1.500.000", "2,5 jt"). A range must lie in one band, a "less than" must
 * be the lowest band and a "more than" the highest; an amount under 1,000 with no unit names no
 * band (rupiah or thousands, nobody can tell), except 0.
 */
export function bacaPendapatan(text: string | null | undefined): Hasil<PendapatanBulanan> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  if ((PENDAPATAN_BULANAN as readonly string[]).includes(t)) return nilai(t as PendapatanBulanan)
  const s = t
    .toLowerCase()
    .replace(/rp\.?/g, ' ')
    .replace(/[,.]\s*-/g, ' ')
    .replace(/,00(?!\d)/g, '')
  const jumlah = jumlahRupiah(s)
  const [a, b] = jumlah
  if (a === undefined) return GAGAL
  if (b !== undefined) {
    const [bawah, atas] = a <= b ? [a, b] : [b, a]
    const pita = bandPendapatan(bawah)
    return pita !== null && pita === bandPendapatan(Math.max(bawah, atas - 1)) ? nilai(pita) : GAGAL
  }
  if (/<|≤|kurang|di ?bawah/.test(s)) return a <= 500_000 ? nilai('kurang_500rb') : GAGAL
  if (/>|≥|lebih|di ?atas/.test(s)) return a >= 6_000_000 ? nilai('lebih_6jt') : GAGAL
  if (a > 0 && a < 1000) return GAGAL
  const pita = bandPendapatan(a)
  return pita === null ? GAGAL : nilai(pita)
}

const ASURANSI = daftar<AsuransiKesehatan>(ASURANSI_KESEHATAN, ui.enum.asuransiKesehatan, {
  pbi: 'bpjs_pbi',
  'bpjs pbi': 'bpjs_pbi',
  'pbi jk': 'bpjs_pbi',
  'bpjs pbi jk': 'bpjs_pbi',
  'jkn pbi': 'bpjs_pbi',
  kis: 'bpjs_pbi',
  'bpjs kis': 'bpjs_pbi',
  'kartu indonesia sehat': 'bpjs_pbi',
  jamkesmas: 'bpjs_pbi',
  jamkesda: 'bpjs_pbi',
  'bpjs gratis': 'bpjs_pbi',
  'bpjs bantuan': 'bpjs_pbi',
  'bpjs bantuan pemerintah': 'bpjs_pbi',
  'bpjs pemerintah': 'bpjs_pbi',
  'asuransi swasta': 'swasta',
  'asuransi pribadi': 'swasta',
  'asuransi lain': 'swasta',
  prudential: 'swasta',
  allianz: 'swasta',
  axa: 'swasta',
  manulife: 'swasta',
  aia: 'swasta',
  inhealth: 'swasta',
  'mandiri inhealth': 'swasta',
  'tidak punya': 'tidak_ada',
  'tidak memiliki': 'tidak_ada',
  'belum ada': 'tidak_ada',
  'belum punya': 'tidak_ada',
  'tdk ada': 'tidak_ada',
  'tanpa asuransi': 'tidak_ada',
  tidak: 'tidak_ada',
  belum: 'tidak_ada',
})

const KELAS: Readonly<Record<string, 1 | 2 | 3>> = { '1': 1, '2': 2, '3': 3, i: 1, ii: 2, iii: 3 }

/** Health insurance: "BPJS 1", "BPJS Kls III" and "Kelas 2" by their class; "BPJS" alone names none. */
export function bacaAsuransi(text: string | null | undefined): Hasil<AsuransiKesehatan> {
  const hasil = cari(text, ASURANSI)
  if (hasil.jenis !== 'gagal') return hasil
  const key = kunciTeks(rapikanTeks(text) ?? '')
  const m =
    /^(?:(?:bpjs|jkn)(?: kesehatan)?(?: mandiri)? )?(?:(?:kelas|kls|kl) )?(1|2|3|i|ii|iii)$/.exec(
      key,
    )
  const kelas = m?.[1] === undefined ? undefined : KELAS[m[1]]
  // A bare "1" is no insurance: a class needs BPJS or "Kelas" before it.
  if (kelas === undefined || key === m?.[1]) return GAGAL
  return nilai(`bpjs_kelas_${kelas}` as AsuransiKesehatan)
}

// ---------------------------------------------------------------------------
// The family's housing, the credit union, the kinds of baptism and marriage
// ---------------------------------------------------------------------------

const RUMAH = daftar<StatusRumah>(STATUS_RUMAH, ui.enum.statusRumah, {
  'rumah sendiri': 'milik_sendiri',
  'rumah pribadi': 'milik_sendiri',
  'milik pribadi': 'milik_sendiri',
  'rumah milik sendiri': 'milik_sendiri',
  'hak milik': 'milik_sendiri',
  pribadi: 'milik_sendiri',
  sendiri: 'milik_sendiri',
  sewa: 'sewa_kontrak',
  kontrak: 'sewa_kontrak',
  kontrakan: 'sewa_kontrak',
  'rumah sewa': 'sewa_kontrak',
  'rumah kontrakan': 'sewa_kontrak',
  'rumah kontrak': 'sewa_kontrak',
  kos: 'sewa_kontrak',
  kost: 'sewa_kontrak',
  indekos: 'sewa_kontrak',
  numpang: 'menumpang',
  tumpang: 'menumpang',
  'ikut orang tua': 'menumpang',
  'ikut keluarga': 'menumpang',
  'menumpang keluarga': 'menumpang',
  'menumpang orang tua': 'menumpang',
  dinas: 'rumah_dinas',
  'rumah negara': 'rumah_dinas',
  'rumah instansi': 'rumah_dinas',
  'rumah jabatan': 'rumah_dinas',
  asrama: 'rumah_dinas',
  mess: 'rumah_dinas',
  lain: 'lainnya',
  'lain lain': 'lainnya',
})

export function bacaStatusRumah(text: string | null | undefined): Hasil<StatusRumah> {
  return cari(text, RUMAH)
}

/**
 * The credit union (the sheet's "Anggota Credit Union Ndar Sesepok"): "Anggota", ya, sudah; "Bukan",
 * tidak, belum. Under this header "Bukan" is a plain no.
 */
const CU: ReadonlyMap<string, boolean> = new Map([
  ...[
    'anggota',
    'anggota cu',
    'sudah anggota',
    'ya',
    'iya',
    'y',
    'sudah',
    'member',
    'aktif',
    '1',
    'true',
    'v',
    'x',
  ].map((k) => [k, true] as const),
  ...[
    'bukan',
    'bukan anggota',
    'belum anggota',
    'tidak anggota',
    'tidak',
    'tdk',
    'belum',
    'n',
    'no',
    '0',
    'false',
  ].map((k) => [k, false] as const),
])

export function bacaAnggotaCu(text: string | null | undefined): Hasil<boolean> {
  const t = rapikanTeks(text)
  if (t !== null && /^[✓✔☑]$/u.test(t)) return nilai(true)
  return cari(text, CU)
}

const BAPTIS = daftar<JenisBaptis>(JENIS_BAPTIS, ui.enum.jenisBaptis, {
  anak: 'bayi',
  'baptis anak': 'bayi',
  'baptis bayi': 'bayi',
  'anak anak': 'bayi',
  'kanak kanak': 'bayi',
  'baptis dewasa': 'dewasa',
  dws: 'dewasa',
  'baptis bersyarat': 'bersyarat',
  diterima: 'diterima_penuh',
  penerimaan: 'diterima_penuh',
  'penerimaan penuh': 'diterima_penuh',
  'dari gereja lain': 'diterima_penuh',
})

/** A baptism's kind: "Anak" (a child's baptism) is Bayi; a place written here reads as nothing. */
export function bacaJenisBaptis(text: string | null | undefined): Hasil<JenisBaptis> {
  return cari(text, BAPTIS)
}

const PERKAWINAN = daftar<JenisPerkawinan>(JENIS_PERKAWINAN_LUAR, ui.enum.jenisPerkawinanLuar, {
  katholik: 'katolik',
  sakramen: 'katolik',
  sakramental: 'katolik',
  'nikah katolik': 'katolik',
  'perkawinan katolik': 'katolik',
  'sesama katolik': 'katolik',
  'katolik katolik': 'katolik',
  'beda gereja': 'campur_beda_gereja',
  'campur gereja': 'campur_beda_gereja',
  'kawin campur beda gereja': 'campur_beda_gereja',
  'perkawinan campur beda gereja': 'campur_beda_gereja',
  'mixta religio': 'campur_beda_gereja',
  'campur beda agama': 'beda_agama',
  'kawin beda agama': 'beda_agama',
  'perkawinan beda agama': 'beda_agama',
  'disparitas cultus': 'beda_agama',
  'disparitas kultus': 'beda_agama',
})

/** A marriage's kind. "Campur" alone is not read: a mixed marriage is of either kind. */
export function bacaJenisPerkawinan(text: string | null | undefined): Hasil<JenisPerkawinan> {
  return cari(text, PERKAWINAN)
}

// ---------------------------------------------------------------------------
// Education now, the school, the year, the place among the children
// ---------------------------------------------------------------------------

const SAAT_INI = daftar<PendidikanSaatIni>(PENDIDIKAN_SAAT_INI, ui.enum.pendidikanSaatIni, {
  'belum sekolah': 'belum_sekolah',
  'tidak sekolah': 'belum_sekolah',
  'tidak belum sekolah': 'belum_sekolah',
  'tidak bersekolah': 'belum_sekolah',
  'putus sekolah': 'belum_sekolah',
  paud: 'paud_tk',
  tk: 'paud_tk',
  kb: 'paud_tk',
  'kelompok bermain': 'paud_tk',
  playgroup: 'paud_tk',
  'taman kanak kanak': 'paud_tk',
  'tk paud': 'paud_tk',
  'sekolah dasar': 'sd',
  mi: 'sd',
  sdn: 'sd',
  sdi: 'sd',
  sdk: 'sd',
  'sd inpres': 'sd',
  'sd negeri': 'sd',
  sdlb: 'sd',
  sltp: 'smp',
  mts: 'smp',
  smpn: 'smp',
  smpk: 'smp',
  smplb: 'smp',
  'sekolah menengah pertama': 'smp',
  sma: 'sma_smk',
  smk: 'sma_smk',
  slta: 'sma_smk',
  smu: 'sma_smk',
  ma: 'sma_smk',
  sman: 'sma_smk',
  smkn: 'sma_smk',
  smak: 'sma_smk',
  stm: 'sma_smk',
  smea: 'sma_smk',
  spg: 'sma_smk',
  smalb: 'sma_smk',
  'sekolah menengah atas': 'sma_smk',
  'sekolah menengah kejuruan': 'sma_smk',
  d1: 'd1_d3',
  d2: 'd1_d3',
  d3: 'd1_d3',
  'd i': 'd1_d3',
  'd ii': 'd1_d3',
  'd iii': 'd1_d3',
  diploma: 'd1_d3',
  akademi: 'd1_d3',
  d4: 'd4_s1',
  's 1': 'd4_s1',
  'd iv': 'd4_s1',
  'strata 1': 'd4_s1',
  kuliah: 'd4_s1',
  'sedang kuliah': 'd4_s1',
  mahasiswa: 'd4_s1',
  mahasiswi: 'd4_s1',
  sarjana: 'd4_s1',
  universitas: 'd4_s1',
  'perguruan tinggi': 'd4_s1',
  'sekolah tinggi': 'd4_s1',
  institut: 'd4_s1',
  stkip: 'd4_s1',
  kampus: 'd4_s1',
  's 2': 's2',
  magister: 's2',
  master: 's2',
  pascasarjana: 's2',
  'strata 2': 's2',
  's 3': 's3',
  doktor: 's3',
  doktoral: 's3',
  'strata 3': 's3',
})

/** The school a person attends now: the level, from the whole cell or its start. */
export function bacaPendidikanSaatIni(text: string | null | undefined): Hasil<PendidikanSaatIni> {
  return bacaDenganAwalan(text, (t) => cari(t, SAAT_INI))
}

/**
 * The level a longer cell was read as from its start ("SMA Negeri 1 Agats": SMA/SMK), in the
 * list's words, for the row's note (review minor 3); null when the cell was read whole, or not
 * at all. `jenis`: Pendidikan terakhir (`dukcapil`, a Dukcapil code) or saat ini.
 */
export function levelDariAwalan(
  text: string | null | undefined,
  jenis: 'dukcapil' | 'saat_ini',
): string | null {
  const t = rapikanTeks(text)
  if (t === null) return null
  if (jenis === 'saat_ini') {
    const baca = (s: string) => cari(s, SAAT_INI)
    if (baca(t).jenis !== 'gagal') return null
    const kode = bacaAwalan(t, baca)
    return kode === null ? null : (ui.enum.pendidikanSaatIni[kode] ?? kode)
  }
  const baca = (s: string) => bacaKode(s, PENDIDIKAN, PENDIDIKAN_ALIAS)
  if (baca(t).jenis !== 'gagal') return null
  const kode = bacaAwalan(t, baca)
  return kode === null ? null : (PENDIDIKAN.entri.find((e) => e.kode === kode)?.label ?? kode)
}

/**
 * The school a cell names after its level ("SMA Negeri 1 Agats"), as written, for Nama sekolah;
 * null when the cell is a level alone or does not start with one.
 */
export function sekolahDariPendidikan(text: string | null | undefined): string | null {
  const t = rapikanTeks(text)
  if (t === null) return null
  const dukcapil = (s: string) => bacaKode(s, PENDIDIKAN, PENDIDIKAN_ALIAS)
  const sekarang = (s: string) => cari(s, SAAT_INI)
  if (dukcapil(t).jenis === 'nilai' || sekarang(t).jenis === 'nilai') return null
  return bacaAwalan(t, sekarang) !== null || bacaAwalan(t, dukcapil) !== null ? t : null
}

const URUTAN: Readonly<Record<string, number>> = {
  pertama: 1,
  kesatu: 1,
  sulung: 1,
  kedua: 2,
  ketiga: 3,
  keempat: 4,
  kelima: 5,
  keenam: 6,
  ketujuh: 7,
  kedelapan: 8,
  kesembilan: 9,
  kesepuluh: 10,
}

/** "Anak Ke": 2, "2.0", "ke-3", "kedua"; 1 to 30. */
export function bacaAnakKe(text: string | null | undefined): Hasil<number> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const m = /^'?(?:anak\s*)?(?:ke\s*[-.]?\s*)?(\d{1,3})(?:[.,]0+)?$/i.exec(t)
  const kata = kunciTeks(t).replace(/^anak /, '')
  const n = m?.[1] !== undefined ? Number(m[1]) : URUTAN[kata]
  return n !== undefined && n >= 1 && n <= ANAK_KE_MAKS ? nilai(n) : GAGAL
}

/**
 * A year ("2011", "2011.0", a date's year, Excel's serial of a date): 1900 to 2100; 1/1/0001 is
 * empty.
 */
export function bacaTahun(text: string | null | undefined): Hasil<number> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const m = /^'?(\d{4})(?:[.,]0+)?$/.exec(t)
  let tahun: number | null = m?.[1] === undefined ? null : Number(m[1])
  if (tahun === null) {
    const tanggal = bacaTanggal(t)
    // The tools' "no date" (1/1/0001) is no year either (review minor 2).
    if (tanggal.jenis === 'kosong') return KOSONG
    if (tanggal.jenis === 'nilai') tahun = Number(tanggal.nilai.slice(0, 4))
  }
  return tahun !== null && tahun >= TAHUN_LULUS_MIN && tahun <= TAHUN_LULUS_MAKS
    ? nilai(tahun)
    : GAGAL
}
